import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Which fund has lower fees, SCHD or VYM?',
    a: "Vanguard's VYM is slightly more economical at a 0.04% annual expense ratio, compared to Schwab's SCHD at 0.06%. This minor 2-basis-point difference amounts to just $2 per year on a $10,000 portfolio — meaningful at scale, immaterial for most retail accounts.",
  },
  {
    q: "Why doesn't VYM hold REITs?",
    a: 'According to the FTSE Index methodology rules, Real Estate Investment Trusts are categorised under an independent asset class rather than standard high-yield equities. VYM mirrors this index exactly, so it carries zero exposure to real-estate pass-through income. SCHD, which tracks the Dow Jones U.S. Dividend 100, also screens out REITs.',
  },
  {
    q: 'Which has the higher current dividend yield?',
    a: "SCHD's trailing-twelve-month yield runs roughly 3.2-3.5%, while VYM sits closer to 2.2-2.4%. The gap reflects SCHD's deliberate quality + yield concentration: by ranking 100 stocks on cash-flow, ROE, dividend growth, and payout ratio before picking, it ends up holding higher-yielding names than VYM's market-cap-weighted broad basket.",
  },
  {
    q: 'How much overlap is there between SCHD and VYM?',
    a: "Because VYM holds roughly 590 stocks and SCHD holds 100, almost every SCHD name is also inside VYM. The overlap by holdings count is around 85-90%, but because the weighting schemes differ (modified market-cap for SCHD's 100, pure market-cap for VYM's 590), the overlap by portfolio weight is closer to 40-50%. Owning both still creates a useful core-and-satellite tilt.",
  },
  {
    q: 'Can I own both SCHD and VYM?',
    a: 'Yes, and many DiviDrip users do. The typical setup is VYM as a broad market-cap foundation (giant-cap stability, the lowest possible fee tier) with SCHD overlaid on top to tilt the combined portfolio toward higher-quality, higher-yielding dividend payers. The combination raises the blended yield without giving up Vanguard\u2019s diversification anchor.',
  },
];

export default function LearnSchdVsVym() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="SCHD vs VYM — Concentrated Quality vs Broad Market Yield (2026) | DiviDrip"
      description="SCHD vs VYM side-by-side: yield, holdings, fees, index methodology. Which low-cost dividend ETF fits — and when owning both makes sense."
      canonical="https://twylightcrow.com/learn/schd-vs-vym"
      guidePath="/learn/schd-vs-vym"
      sectionLabel="Dividend Tiers & ETF Face-offs"
      sectionAnchor="tiers"
      kicker="Learn · ETF face-off"
      h1="SCHD vs VYM — Concentrated Quality vs Broad Market Yield"
      datePublished="2026-05-26"
      jsonLd={jsonLd}
      related={[
        { to: '/true-return/schd-vs-vym', label: 'True Return Chart — SCHD vs VYM, $10K after dividends and inflation' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/learn/evaluating-dividend-etfs', label: 'How to evaluate a dividend ETF' },
        { to: '/learn/covered-call-etfs', label: 'Covered call ETFs explained — JEPI, JEPQ, QYLD, XYLD' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers to check before any buy' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/screener', label: 'Stock Screener — filter by yield, sector, frequency' },
      ]}
    >
      <p>
        If you want a pure, low-cost domestic dividend fund, your final choice almost always
        comes down to Schwab&apos;s <strong>SCHD</strong> or Vanguard&apos;s{' '}
        <strong>VYM</strong> (Vanguard High Dividend Yield ETF). They charge nearly identical,
        razor-thin fees, but they construct their portfolios using two entirely different
        philosophies: <em>sniper-like concentration</em> versus a <em>wide-sweeping net</em>.
      </p>

      <h2>Side-by-side</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Metric</th>
            <th className="text-left font-semibold">SCHD</th>
            <th className="text-left font-semibold">VYM</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Underlying index</td>
            <td>Dow Jones U.S. Dividend 100</td>
            <td>FTSE High Dividend Yield</td>
          </tr>
          <tr>
            <td className="font-semibold">Distribution frequency</td>
            <td>Quarterly</td>
            <td>Quarterly</td>
          </tr>
          <tr>
            <td className="font-semibold">Trailing 12-month yield (approx)</td>
            <td>~3.2–3.5%</td>
            <td>~2.2–2.4%</td>
          </tr>
          <tr>
            <td className="font-semibold">Expense ratio</td>
            <td>0.06%</td>
            <td>0.04%</td>
          </tr>
          <tr>
            <td className="font-semibold">Number of holdings</td>
            <td>~100</td>
            <td>~590</td>
          </tr>
          <tr>
            <td className="font-semibold">Beta (5Y monthly, vs S&amp;P 500)</td>
            <td>~0.74</td>
            <td>~0.76</td>
          </tr>
          <tr>
            <td className="font-semibold">Best-fit use case</td>
            <td>Quality &amp; yield focus</td>
            <td>Ultra-broad value exposure</td>
          </tr>
        </tbody>
      </table>

      <h2>How the index rules differ (60 seconds)</h2>
      <p>
        <strong>VYM</strong> casts a massive net. It takes the entire universe of U.S.
        dividend-paying stocks, ranks them by forecasted 12-month yield, cuts out the bottom
        half, and market-cap weights the remaining group. It applies <em>zero</em> quality
        screens around debt, cash flow, or return on equity.
      </p>
      <p>
        <strong>SCHD</strong> is much stricter. It first filters for firms with a 10-year
        track record of consecutive dividend payments, then scores those survivors across
        four fundamental quality metrics (cash-flow-to-debt, return on equity, dividend
        yield, and 5-year dividend growth) before selecting the top 100. Both indexes
        explicitly exclude Real Estate Investment Trusts.
      </p>

      <h2>When to pick SCHD</h2>
      <ul>
        <li>You want a higher current starting dividend yield.</li>
        <li>
          You believe strict financial-quality filters (cash flow, ROE, dividend-growth
          history) protect against corporate defaults or dividend cuts.
        </li>
        <li>
          You&apos;re comfortable with the top 10 holdings making up roughly 40% of the
          fund&apos;s weight — concentration is a feature, not a bug, in SCHD&apos;s design.
        </li>
      </ul>

      <h2>When to pick VYM</h2>
      <ul>
        <li>
          You want absolute diversification across roughly 590 stocks, minimising
          single-company crash risk.
        </li>
        <li>
          You prefer the absolute lowest possible fee tier — Vanguard prices VYM at just
          0.04%.
        </li>
        <li>
          You want exposure to massive financial and tech stalwarts like JPMorgan Chase and
          Broadcom that may not pass SCHD&apos;s proprietary screens.
        </li>
      </ul>

      <h2>The case for owning both</h2>
      <p>
        Unlike the SCHD/DGRO pairing, combining SCHD and VYM creates a high degree of
        structural overlap. Because VYM holds roughly 590 names, it naturally swallows up
        almost all of SCHD&apos;s 100 selections. That said, owning both can act as a
        <em> core-and-satellite</em> setup: VYM provides a broad, stable market-cap
        foundation, while SCHD overlays a quality-and-yield tilt on top. The blended
        portfolio ends up with a higher yield than VYM alone and broader diversification
        than SCHD alone.
      </p>

      <h2>Compare them live</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search <strong>SCHD</strong>, click the
        row to open the Stock Modal, then tap the <em>Compare</em> button and pick VYM as
        slot B. When you view the Overlay Chart, watch the historical distribution growth
        lines. VYM&apos;s trend is smooth and gradually upward thanks to its massive
        basket size; SCHD&apos;s line climbs at a noticeably steeper angle over multi-year
        horizons because its focused quality-and-growth requirements concentrate exposure
        in faster dividend growers.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not investment advice. Yields, holdings, and expense ratios can
        change; always check the fund&apos;s latest factsheet at Schwab or Vanguard and the
        live numbers in DiviDrip before buying.
      </p>
    </LearnPage>
  );
}
